/**
 * "Customize view" for the member list (TRUS-568).
 *
 * Lets each user pick which of the ORG-CONFIGURED list columns and advanced
 * filters appear on their own screen. Only fields the org set up in Field
 * Configuration are offered — never a standard list. Persisted per user (and
 * per org) through the existing opaque-JSON store /api/user-filter-preferences
 * under its own page key, so it travels across devices and never collides with
 * the "Save my view" filter-values snapshot.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Settings2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { apiClient } from '@/services/apiClient';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  ConfiguredField,
  EMPTY_SELECTION,
  VIEW_FIELDS_PAGE_KEY,
  ViewFieldSelection,
  applySelection,
  isOrgDefault,
  parseSelection,
  pruneSelection,
} from './memberListViewFields';

/**
 * Loads the user's saved field selection and applies it to the org's
 * configured columns / filters. Until the saved selection arrives (or when
 * there is none) the org configuration is shown unchanged.
 */
export function useMemberListViewFields<C extends ConfiguredField, F extends ConfiguredField>(
  orgColumns: C[],
  orgFilters: F[],
) {
  const [selection, setSelection] = useState<ViewFieldSelection>(EMPTY_SELECTION);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get(`/api/user-filter-preferences/${VIEW_FIELDS_PAGE_KEY}`)
      .then((r) => { if (!cancelled) setSelection(parseSelection(r.data?.filter_state)); })
      .catch(() => { /* no saved selection → org default */ });
    return () => { cancelled = true; };
  }, []);

  const visibleColumns = useMemo(
    () => applySelection(orgColumns, selection.hidden_columns),
    [orgColumns, selection.hidden_columns],
  );
  const visibleFilters = useMemo(
    () => applySelection(orgFilters, selection.hidden_filters),
    [orgFilters, selection.hidden_filters],
  );

  const save = useCallback(async (next: ViewFieldSelection) => {
    const pruned = pruneSelection(next, orgColumns, orgFilters);
    if (isOrgDefault(pruned, orgColumns, orgFilters)) {
      await apiClient.delete(`/api/user-filter-preferences/${VIEW_FIELDS_PAGE_KEY}`);
      setSelection(EMPTY_SELECTION);
      return;
    }
    await apiClient.put(`/api/user-filter-preferences/${VIEW_FIELDS_PAGE_KEY}`, {
      filter_state: pruned,
    });
    setSelection(pruned);
  }, [orgColumns, orgFilters]);

  return { selection, visibleColumns, visibleFilters, save };
}

interface CustomizerProps {
  orgColumns: ConfiguredField[];
  orgFilters: ConfiguredField[];
  selection: ViewFieldSelection;
  onSave: (next: ViewFieldSelection) => Promise<void>;
}

function FieldChecklist({
  title, fields, hidden, onToggle, emptyText,
}: {
  title: string;
  fields: ConfiguredField[];
  hidden: Set<string>;
  onToggle: (key: string) => void;
  emptyText: string;
}) {
  return (
    <div>
      <div className="text-[10px] font-medium text-gray-500 mb-2 uppercase tracking-wider">{title}</div>
      {fields.length === 0 ? (
        <p className="text-xs text-gray-500">{emptyText}</p>
      ) : (
        <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
          {fields.map((f) => (
            <li key={f.field_key}>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!hidden.has(f.field_key)}
                  onChange={() => onToggle(f.field_key)}
                />
                <span>{f.display_name || f.field_key}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function MemberListViewCustomizer({ orgColumns, orgFilters, selection, onSave }: CustomizerProps) {
  const { t } = useAppTranslation();
  const [open, setOpen] = useState(false);
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
  const [hiddenFilters, setHiddenFilters] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openDialog = () => {
    setHiddenCols(new Set(selection.hidden_columns));
    setHiddenFilters(new Set(selection.hidden_filters));
    setError(null);
    setOpen(true);
  };

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void) => (key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    setter(next);
  };

  const persist = async (next: ViewFieldSelection) => {
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'unknown error');
    } finally {
      setSaving(false);
    }
  };

  // Nothing to personalise when the org configured no columns and no filters.
  if (orgColumns.length === 0 && orgFilters.length === 0) return null;

  const customized = !isOrgDefault(selection, orgColumns, orgFilters);

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
        title={t('members.customizeViewHelp', "Choose which of your organization's fields you see as columns and filters")}
      >
        <Settings2 className="w-3.5 h-3.5" />
        {t('members.customizeView', 'Customize view')}
        {customized && <span className="text-[10px] text-gray-500">({t('members.customized', 'customized')})</span>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('members.customizeView', 'Customize view')}</DialogTitle>
            <DialogDescription>
              {t(
                'members.customizeViewDescription',
                "Pick which of your organization's configured fields appear in your member list. This only changes your own view.",
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <FieldChecklist
              title={t('members.customizeColumns', 'Columns')}
              fields={orgColumns}
              hidden={hiddenCols}
              onToggle={toggle(hiddenCols, setHiddenCols)}
              emptyText={t('members.customizeNoColumns', 'Your organization has not configured any list columns.')}
            />
            <FieldChecklist
              title={t('members.customizeFilters', 'Filters')}
              fields={orgFilters}
              hidden={hiddenFilters}
              onToggle={toggle(hiddenFilters, setHiddenFilters)}
              emptyText={t('members.customizeNoFilters', 'Your organization has not configured any filters.')}
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => persist(EMPTY_SELECTION)}
            >
              {t('members.resetToOrgDefault', 'Reset to organization default')}
            </Button>
            <Button
              disabled={saving}
              onClick={() => persist({
                hidden_columns: Array.from(hiddenCols),
                hidden_filters: Array.from(hiddenFilters),
              })}
            >
              {t('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
